import * as Sentry from '@sentry/vue'
import type { App } from 'vue'
import type { Router } from 'vue-router'
import { getStoredConsent } from './consent'
import type { ConsentPreferences } from '@/components/ConsentBanner.vue'
import { useApi } from '@/config/apiFetcher'
import { useJsmTickets } from '@/composables/useJsmTickets'

const JSM_BASE = import.meta.env.VITE_JSM_API_ENDPOINT as string | undefined

let sentryInitialized = false
let appVersion = 'unknown'
let currentConsent: ConsentPreferences | null = null
let storedRouter: Router | null = null

// Sample rates per environment
const environmentSampleRates: Record<
  string,
  { traces: number; profiles: number; replays: number }
> = {
  production: { traces: 0.2, profiles: 0.1, replays: 0.1 },
  beta: { traces: 0.5, profiles: 0.25, replays: 0.2 },
  alpha: { traces: 0.8, profiles: 0.5, replays: 0.3 },
  test: { traces: 1.0, profiles: 1.0, replays: 0.5 },
  preview: { traces: 1.0, profiles: 1.0, replays: 0.5 },
  development: { traces: 1.0, profiles: 1.0, replays: 0.5 },
}

function getSampleRates(environment: string) {
  return environmentSampleRates[environment] || environmentSampleRates.development
}

interface VersionInfo {
  versionName: string
  versionCode: number
  buildDate: string
  branch: string
  commit: string
  releaseType: string
}

// Fetch version from deployed version.json
async function fetchAppVersion(): Promise<VersionInfo | null> {
  try {
    const response = await fetch('/api/version.json')
    if (!response.ok) return null
    return await response.json()
  } catch {
    return null
  }
}

// Build integrations based on consent
function buildIntegrations(consent: ConsentPreferences | null, router: Router) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const integrations: any[] = []

  // Performance monitoring (if consented)
  if (consent?.performanceMonitoring !== false) {
    integrations.push(Sentry.browserTracingIntegration({ router }))
    // Profiling for detailed function-level performance data
    integrations.push(Sentry.browserProfilingIntegration())
  }

  // Session replay (only if explicitly consented)
  if (consent?.sessionReplay === true) {
    integrations.push(
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    )
  }

  // Feedback widget (always available, but respects user identification consent)
  integrations.push(
    Sentry.feedbackIntegration({
      colorScheme: 'dark',
      autoInject: false, // We use custom trigger buttons

      // Branding
      buttonLabel: 'Report an Issue',
      submitButtonLabel: 'Send Feedback',
      cancelButtonLabel: 'Cancel',
      formTitle: 'Report an Issue',
      messageLabel: 'Description',
      messagePlaceholder: 'Describe what happened or what went wrong...',
      nameLabel: 'Name',
      namePlaceholder: 'Your name',
      emailLabel: 'Email',
      emailPlaceholder: 'your.email@example.com',
      successMessageText: 'Thank you for your feedback!',

      // Features
      enableScreenshot: true,
      showBranding: false,

      // Auto-fill user info (only if consented)
      useSentryUser: consent?.userIdentification
        ? {
            name: 'username',
            email: 'email',
          }
        : undefined,

      // IDI Dashboard Theme Colors (using Sentry's FeedbackTheme properties)
      themeDark: {
        background: '#1a1d21', // Lighter background
        foreground: '#ffffff', // White text
        accentBackground: '#20a4f3', // IDI Blue for buttons
        accentForeground: '#ffffff', // White text on buttons
        successColor: '#0a8f30', // Success green
        errorColor: '#e53935', // Error red
        outline: '1px solid #20a4f3', // IDI Blue focus outline
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)', // Subtle shadow
      },
    }),
  )

  return integrations
}

export function initSentry(app: App, router: Router) {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development'

  if (!dsn) {
    console.warn('[Sentry] DSN not configured. Sentry is disabled.')
    return
  }

  storedRouter = router
  currentConsent = getStoredConsent()

  // Determine sample rates based on consent and environment
  const hasReplayConsent = currentConsent?.sessionReplay === true
  const hasPerformanceConsent = currentConsent?.performanceMonitoring !== false
  const rates = getSampleRates(environment)

  Sentry.init({
    app,
    dsn,
    environment,
    release: appVersion,
    integrations: buildIntegrations(currentConsent, router),

    // Performance monitoring (0 if not consented)
    tracesSampleRate: hasPerformanceConsent ? rates.traces : 0,

    // Profiling sample rate (0 if not consented)
    profilesSampleRate: hasPerformanceConsent ? rates.profiles : 0,

    // Session replay (0 if not consented)
    replaysSessionSampleRate: hasReplayConsent ? rates.replays : 0,
    replaysOnErrorSampleRate: hasReplayConsent ? 1.0 : 0,

    // Only send PII if user identification is consented
    sendDefaultPii: currentConsent?.userIdentification === true,

    // Filter out noisy errors
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Network request failed',
      'Failed to fetch',
    ],

    // Add custom tags to all events
    initialScope: {
      tags: {
        app: 'idi-fly-dashboard',
      },
    },

    // Before sending an event, check consent and enrich with context
    beforeSend(event, hint) {
      // Block events if error tracking not consented (shouldn't happen but safety check)
      if (currentConsent?.errorTracking === false) {
        return null
      }

      // Add browser info (non-PII)
      if (typeof window !== 'undefined') {
        event.contexts = {
          ...event.contexts,
          browser_info: {
            userAgent: navigator.userAgent,
            language: navigator.language,
            online: navigator.onLine,
            screenWidth: window.screen.width,
            screenHeight: window.screen.height,
            viewportWidth: window.innerWidth,
            viewportHeight: window.innerHeight,
          },
        }
      }

      return event
    },

    // Capture breadcrumbs for debugging
    beforeBreadcrumb(breadcrumb) {
      // Filter out noisy breadcrumbs
      if (breadcrumb.category === 'xhr' && breadcrumb.data?.url?.includes('/health')) {
        return null
      }
      return breadcrumb
    },
  })

  sentryInitialized = true
  console.log('[Sentry] Initialized successfully with DSN:', dsn.substring(0, 30) + '...')

  // Fetch version info and update Sentry tags
  fetchAppVersion().then((versionInfo) => {
    if (versionInfo) {
      appVersion = versionInfo.versionName
      Sentry.setTag('version', versionInfo.versionName)
      Sentry.setTag('version_code', String(versionInfo.versionCode))
      Sentry.setTag('branch', versionInfo.branch)
      Sentry.setTag('release_type', versionInfo.releaseType)
      Sentry.setContext('build_info', {
        version: versionInfo.versionName,
        versionCode: versionInfo.versionCode,
        buildDate: versionInfo.buildDate,
        branch: versionInfo.branch,
        commit: versionInfo.commit,
        releaseType: versionInfo.releaseType,
      })
    }
  })
}

// Update Sentry when consent changes (called from ConsentBanner)
export function updateSentryConsent(consent: ConsentPreferences) {
  currentConsent = consent

  if (!sentryInitialized) return

  // Update PII setting
  // Note: Some settings can't be changed after init, but we can control behavior via beforeSend

  // Add replay integration if newly consented
  if (consent.sessionReplay && storedRouter) {
    const client = Sentry.getClient()
    if (client) {
      const existingReplay = client.getIntegrationByName('Replay')
      if (!existingReplay) {
        Sentry.addIntegration(
          Sentry.replayIntegration({
            maskAllText: true,
            blockAllMedia: true,
          }),
        )
      }
    }
  }

  // Log consent update
  Sentry.addBreadcrumb({
    category: 'consent',
    message: 'User updated consent preferences',
    level: 'info',
    data: {
      errorTracking: consent.errorTracking,
      performanceMonitoring: consent.performanceMonitoring,
      sessionReplay: consent.sessionReplay,
      userIdentification: consent.userIdentification,
    },
  })
}

// Get current app version
export function getAppVersion(): string {
  return appVersion
}

// Check if user identification is allowed
export function isUserIdentificationAllowed(): boolean {
  return currentConsent?.userIdentification === true
}

// Set user context after login (respects consent)
export function setSentryUser(user: {
  id: string
  username: string
  email?: string
  organisation?: string
}) {
  if (!sentryInitialized) return

  // Only set user context if user identification is consented
  if (currentConsent?.userIdentification === true) {
    Sentry.setUser({
      id: user.id,
      username: user.username,
      email: user.email,
    })
    Sentry.setTag('organisation', user.organisation || 'unknown')
  } else {
    // Set anonymous organisation tag only
    Sentry.setTag('organisation', user.organisation || 'unknown')
  }
}

// Clear user context on logout
export function clearSentryUser() {
  if (!sentryInitialized) return
  Sentry.setUser(null)

  // Close any open feedback dialog
  const feedback = Sentry.getFeedback()
  if (feedback) {
    feedback.remove()
  }
}

// Track user actions as breadcrumbs
export function trackUserAction(
  action: string,
  category: string = 'user.action',
  data?: Record<string, any>,
) {
  if (!sentryInitialized) return

  Sentry.addBreadcrumb({
    category,
    message: action,
    level: 'info',
    data,
    timestamp: Date.now() / 1000,
  })
}

// Track page views — tags the current page on all subsequent Sentry events
// and adds a navigation breadcrumb for the session timeline.
export function trackPageView(pageName: string, path: string) {
  if (!sentryInitialized) return

  // Persist the current route as a tag so every error / event carries it
  Sentry.setTag('page', pageName)

  // Attach full route details as structured context
  Sentry.setContext('route', { name: pageName, path })

  Sentry.addBreadcrumb({
    category: 'navigation',
    message: `Viewed ${pageName}`,
    level: 'info',
    data: { path },
    timestamp: Date.now() / 1000,
  })
}

// Track feature usage
export function trackFeatureUsage(feature: string, metadata?: Record<string, any>) {
  if (!sentryInitialized) return

  Sentry.addBreadcrumb({
    category: 'feature',
    message: `Used feature: ${feature}`,
    level: 'info',
    data: metadata,
    timestamp: Date.now() / 1000,
  })
}

// Capture custom error with context
export function captureError(
  error: unknown,
  context?: Record<string, any>,
  level: Sentry.SeverityLevel = 'error',
) {
  if (!sentryInitialized) return

  // Ensure we have an Error object
  const errorObj = error instanceof Error ? error : new Error(String(error))

  Sentry.withScope((scope) => {
    scope.setLevel(level)
    if (context) {
      scope.setContext('additional_info', context)
    }
    Sentry.captureException(errorObj)
  })
}

// Programmatic Feedback API

// Open the feedback widget dialog programmatically
export async function showFeedbackWidget() {
  const feedback = Sentry.getFeedback()
  if (feedback) {
    const dialog = await feedback.createForm()
    dialog.appendToDom()
    dialog.open()
  }
}

// Submit feedback programmatically (for custom feedback forms)
export function submitFeedback(message: string, name?: string, email?: string): string | null {
  if (!sentryInitialized) {
    console.error('[Sentry] Cannot submit feedback: Sentry is not initialized')
    return null
  }

  console.log('[Sentry] Submitting feedback (no attachments):', {
    message: message.substring(0, 50) + '...',
    name,
    email,
  })

  const eventId = Sentry.captureFeedback({
    message,
    name,
    email,
  })

  console.log('[Sentry] Feedback submitted with event ID:', eventId)
  return eventId
}

// Sentry payload limits
const MAX_ATTACHMENT_SIZE = 1 * 1024 * 1024 // 1MB per image (images get compressed)
const MAX_MEDIA_ATTACHMENT_SIZE = 15 * 1024 * 1024 // 15MB per audio/video file
const MAX_TOTAL_PAYLOAD = 18 * 1024 * 1024 // 18MB total (Sentry hard cap is ~20MB)

// Compress image data using canvas
async function compressImage(
  data: Uint8Array,
  contentType: string,
  maxSize: number = MAX_ATTACHMENT_SIZE,
): Promise<Uint8Array | null> {
  // Only compress images
  if (!contentType.startsWith('image/')) {
    return data.length <= maxSize ? data : null
  }

  try {
    // Create blob from data
    const blob = new Blob([data], { type: contentType })
    const imageBitmap = await createImageBitmap(blob)

    // Calculate scaling factor to reduce size
    let quality = 0.8
    let scale = 1.0
    let compressedData: Uint8Array | null = null

    // Try progressively lower quality/size until under limit
    for (let attempt = 0; attempt < 5; attempt++) {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      if (!ctx) return null

      canvas.width = Math.floor(imageBitmap.width * scale)
      canvas.height = Math.floor(imageBitmap.height * scale)
      ctx.drawImage(imageBitmap, 0, 0, canvas.width, canvas.height)

      const compressedBlob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob((blob) => resolve(blob), 'image/jpeg', quality)
      })

      if (!compressedBlob) return null

      const buffer = await compressedBlob.arrayBuffer()
      compressedData = new Uint8Array(buffer)

      if (compressedData.length <= maxSize) {
        console.log(
          `[Sentry] Compressed image from ${data.length} to ${compressedData.length} bytes (${Math.round((compressedData.length / data.length) * 100)}%)`,
        )
        return compressedData
      }

      // Reduce quality and scale for next attempt
      quality *= 0.7
      scale *= 0.8
    }

    console.warn('[Sentry] Could not compress image enough, skipping')
    return null
  } catch (error) {
    console.error('[Sentry] Error compressing image:', error)
    return null
  }
}

export interface FeedbackResult {
  /** Sentry event ID — absent when Sentry is not initialised (JSM-only submission) */
  sentryEventId?: string
  /** JSM ticket key (e.g. "FLY3D-123"), present only if JSM creation succeeded */
  jsmTicketKey?: string
}

// Submit feedback with attachments (voice, video, screenshots)
export async function submitFeedbackWithAttachments(
  message: string,
  attachments: Array<{ filename: string; data: Uint8Array; contentType: string }>,
  name?: string,
  email?: string,
): Promise<FeedbackResult> {
  const client = Sentry.getClient()
  const sentryAvailable = sentryInitialized && !!client

  if (!sentryAvailable) {
    console.warn('[Sentry] Sentry is not initialized — will attempt JSM-only submission', {
      sentryInitialized,
      hasClient: !!client,
    })
    // Graceful degradation: if JSM is configured, submit there and succeed
    if (!JSM_BASE) {
      throw new Error('Feedback service unavailable: neither Sentry nor JSM is configured')
    }
  }

  // Get user info if available and consented (use provided values or fall back to scope)
  const user = Sentry.getCurrentScope().getUser()
  const finalName = name || user?.username
  const finalEmail = email || user?.email

  console.log('[Sentry] Processing feedback with attachments:', {
    message: message.substring(0, 50) + (message.length > 50 ? '...' : ''),
    attachmentsCount: attachments.length,
    attachmentSizes: attachments.map((a) => ({ name: a.filename, size: a.data.length })),
    name: finalName,
    email: finalEmail,
  })

  // Process attachments - compress images, skip oversized files
  const processedAttachments: Array<{ filename: string; data: Uint8Array; contentType: string }> =
    []
  const skippedAttachments: string[] = []
  let totalSize = 0

  for (const attachment of attachments) {
    // Only send images to Sentry; audio/video go to S3
    if (!attachment.contentType.startsWith('image/')) continue

    let processedData: Uint8Array | null = attachment.data

    if (attachment.contentType.startsWith('image/')) {
      // Compress images to fit under the image attachment cap
      processedData = await compressImage(attachment.data, attachment.contentType)
    } else if (attachment.data.length > MAX_ATTACHMENT_SIZE) {
      console.warn(
        `[Sentry] Skipping oversized attachment: ${attachment.filename} (${attachment.data.length} bytes)`,
      )
      skippedAttachments.push(`${attachment.filename} (exceeds size limit)`)
      processedData = null
    }

    if (processedData && totalSize + processedData.length <= MAX_TOTAL_PAYLOAD) {
      processedAttachments.push({
        filename: attachment.filename.replace(/\.(png|gif|webp)$/i, '.jpg'), // Update extension if compressed
        data: processedData,
        contentType: attachment.contentType.startsWith('image/')
          ? 'image/jpeg'
          : attachment.contentType,
      })
      totalSize += processedData.length
      console.log(
        `[Sentry] Added attachment: ${attachment.filename} (${processedData.length} bytes)`,
      )
    } else if (processedData) {
      console.warn(
        `[Sentry] Skipping attachment (would exceed total limit): ${attachment.filename}`,
      )
      skippedAttachments.push(`${attachment.filename} (payload limit reached)`)
    }
  }

  // Append note about skipped attachments to message
  let finalMessage = message
  if (skippedAttachments.length > 0) {
    finalMessage += `\n\n---\nNote: Some attachments were not included:\n- ${skippedAttachments.join('\n- ')}`
  }

  console.log('[Sentry] Submitting feedback:', {
    attachmentsIncluded: processedAttachments.length,
    attachmentsSkipped: skippedAttachments.length,
    totalPayloadSize: totalSize,
  })

  // -------------------------------------------------------------------------
  // Step 1: Create JSM ticket first (so we can embed the key in the Sentry event)
  // -------------------------------------------------------------------------
  let jsmTicketKey: string | undefined

  if (JSM_BASE) {
    try {
      const api = useApi()
      const jsmRes = await api.post<{ ticketKey: string; ticketUrl: string }>(
        `${JSM_BASE}`,
        {
          summary: message.substring(0, 255),
          description: message,
          name: finalName,
          email: finalEmail,
        },
      )
      jsmTicketKey = jsmRes.data?.ticketKey

      if (jsmTicketKey) {
        console.log('[JSM] Ticket created:', jsmTicketKey)
        // Augment message with JSM ticket reference
        const ticketUrl = `https://idroneinnovations.atlassian.net/servicedesk/customer/portal/1/${jsmTicketKey}`
        finalMessage += `\n\n---\nJSM Ticket: [${jsmTicketKey}](${ticketUrl})`

        // Persist ticket to localStorage
        try {
          const { saveTicket } = useJsmTickets()
          saveTicket({
            key: jsmTicketKey,
            summary: message.substring(0, 120),
            submittedAt: new Date().toISOString(),
          })
        } catch (e) {
          console.warn('[JSM] Failed to save ticket to composable', e)
        }

        // Upload ALL attachments (including screenshots) to S3 in parallel,
        // then post a single summary comment so support knows files exist.
        // Images are intentionally included here — they must also appear in
        // the JSM thread, even though they are separately sent to Sentry.
        // The JSM comment intentionally lists only filenames (not the
        // auth-gated API URLs) because support agents in Jira cannot supply
        // a Cognito JWT — they access files via the IDI-FLY app.
        if (attachments.length > 0) {
          try {
            const { uploadAttachment } = useJsmTickets()
            const api2 = useApi()

            // Run all S3 uploads in parallel — avoids serial round-trips for
            // multi-file submissions (especially large drone video files).
            const uploadResults = await Promise.allSettled(
              attachments.map(async (att) => {
                const blob = new Blob([att.data], { type: att.contentType })
                const result = await uploadAttachment(jsmTicketKey!, {
                  filename: att.filename,
                  mimeType: att.contentType,
                  size: att.data.byteLength,
                  blob,
                })
                return result
                  ? { filename: att.filename, viewUrl: result.viewUrl, ok: true }
                  : { filename: att.filename, viewUrl: '', ok: false }
              }),
            )

            const succeeded = uploadResults
              .filter(
                (r): r is PromiseFulfilledResult<{ filename: string; viewUrl: string; ok: boolean }> =>
                  r.status === 'fulfilled' && r.value.ok,
              )
              .map((r) => r.value)

            const failed = uploadResults
              .filter((r) => r.status === 'rejected' || (r.status === 'fulfilled' && !r.value.ok))
              .map((r) =>
                r.status === 'rejected'
                  ? 'unknown'
                  : (r as PromiseFulfilledResult<{ filename: string; viewUrl: string; ok: boolean }>).value.filename,
              )

            if (succeeded.length > 0) {
              // Include 7-day signed CloudFront URLs so support agents can open
              // files directly from Jira — no Cognito token required.
              let commentBody = `${succeeded.length} attachment(s) uploaded via IDI-FLY:\n`
              commentBody += succeeded.map((f) => `- [${f.filename}](${f.viewUrl})`).join('\n')
              if (failed.length > 0) {
                commentBody += `\n\nFailed to upload: ${failed.join(', ')}`
              }
              await api2.post(`${JSM_BASE}/${jsmTicketKey}/reply`, { body: commentBody })
            }
          } catch (e) {
            console.warn('[JSM] Attachment upload failed (non-fatal):', e)
          }
        }
      }
    } catch (jsmError) {
      // Non-fatal: log and continue with Sentry submission
      console.warn('[JSM] Ticket creation failed (Sentry will still be submitted):', jsmError)
    }
  }

  // -------------------------------------------------------------------------
  // Step 2: Submit to Sentry with (possibly augmented) message
  // -------------------------------------------------------------------------
  if (!sentryAvailable) {
    // Sentry not available but JSM succeeded — return without a Sentry event ID
    console.warn('[Sentry] Skipping Sentry submission (not initialised). JSM ticket:', jsmTicketKey)
    if (!jsmTicketKey) {
      throw new Error('Failed to submit feedback: both Sentry and JSM submission failed')
    }
    return { jsmTicketKey }
  }

  try {
    let eventId: string = ''

    // If we have attachments, use scope to add them
    if (processedAttachments.length > 0) {
      await Sentry.withScope(async (scope) => {
        for (const attachment of processedAttachments) {
          scope.addAttachment({
            filename: attachment.filename,
            data: attachment.data,
            contentType: attachment.contentType,
          })
        }

        eventId = Sentry.captureFeedback({
          message: finalMessage,
          name: finalName,
          email: finalEmail,
          url: window.location.href,
          source: 'custom_feedback_modal',
        })
      })
    } else {
      // No attachments, send directly
      eventId = Sentry.captureFeedback({
        message: finalMessage,
        name: finalName,
        email: finalEmail,
        url: window.location.href,
        source: 'custom_feedback_modal',
      })
    }

    console.log('[Sentry] captureFeedback returned event ID:', eventId)

    // Force flush to ensure the event is sent immediately
    console.log('[Sentry] Flushing events...')
    const flushed = await Sentry.flush(10000)
    console.log('[Sentry] Flush completed:', flushed ? 'success' : 'timeout/failed')

    if (!eventId) {
      throw new Error('Failed to capture feedback - no event ID returned')
    }

    return { sentryEventId: eventId, jsmTicketKey }
  } catch (error) {
    console.error('[Sentry] Error submitting feedback:', error)
    throw error
  }
}

// Screen Recording using Sentry Replay

let replayRecording = false

// Start screen recording for feedback (uses Sentry Replay)
export async function startScreenRecording(): Promise<boolean> {
  if (!sentryInitialized) return false

  const replay = Sentry.getReplay()
  if (!replay) {
    console.warn('[Sentry] Replay integration not available')
    return false
  }

  try {
    await replay.start()
    replayRecording = true
    Sentry.addBreadcrumb({
      category: 'feedback',
      message: 'Started screen recording for feedback',
      level: 'info',
    })
    return true
  } catch (error) {
    console.error('[Sentry] Failed to start replay:', error)
    return false
  }
}

// Stop screen recording and get replay ID for feedback
export async function stopScreenRecording(): Promise<string | null> {
  if (!sentryInitialized || !replayRecording) return null

  const replay = Sentry.getReplay()
  if (!replay) return null

  try {
    // Flush the replay to Sentry
    await replay.flush()
    const replayId = replay.getReplayId() ?? null
    replayRecording = false

    Sentry.addBreadcrumb({
      category: 'feedback',
      message: 'Stopped screen recording for feedback',
      level: 'info',
      data: { replayId },
    })

    return replayId
  } catch (error) {
    console.error('[Sentry] Failed to stop replay:', error)
    replayRecording = false
    return null
  }
}

// Check if screen recording is active
export function isScreenRecordingActive(): boolean {
  return replayRecording
}

// Get current replay ID (for linking feedback to replay)
export function getCurrentReplayId(): string | null {
  if (!sentryInitialized) return null
  const replay = Sentry.getReplay()
  return replay?.getReplayId() ?? null
}

// Submit feedback with replay link
export async function submitFeedbackWithReplay(
  message: string,
  replayId?: string | null,
): Promise<void> {
  if (!sentryInitialized) {
    throw new Error('Sentry is not initialized')
  }

  const user = Sentry.getCurrentScope().getUser()
  const actualReplayId = replayId || getCurrentReplayId()

  Sentry.withScope((scope) => {
    if (actualReplayId) {
      scope.setTag('replay_id', actualReplayId)
      scope.setContext('screen_recording', {
        replayId: actualReplayId,
        hasRecording: true,
      })
    }

    Sentry.captureFeedback({
      message,
      name: user?.username,
      email: user?.email,
    })
  })
}

// Check if Sentry is ready for feedback
export function isSentryReady(): boolean {
  const client = Sentry.getClient()
  const ready = sentryInitialized && !!client
  console.log('[Sentry] Ready check:', { sentryInitialized, hasClient: !!client, ready })
  return ready
}

// Test feedback submission (for debugging)
export async function testFeedbackSubmission(): Promise<string | null> {
  console.log('[Sentry] Running test feedback submission...')

  if (!isSentryReady()) {
    console.error('[Sentry] Test failed: Sentry is not ready')
    return null
  }

  try {
    const eventId = Sentry.captureFeedback({
      message: 'Test feedback from FeedbackModal - please ignore',
      name: 'Test User',
      email: 'test@example.com',
      source: 'test',
    })

    console.log('[Sentry] Test feedback event ID:', eventId)

    const flushed = await Sentry.flush(5000)
    console.log('[Sentry] Test flush result:', flushed)

    return eventId
  } catch (error) {
    console.error('[Sentry] Test feedback failed:', error)
    return null
  }
}

export { Sentry }
